import { useDraggable } from '@dnd-kit/core'
import { format, isToday } from 'date-fns'
import { tr } from 'date-fns/locale'
import { MailOpen, Mail as MailIcon, Paperclip, Star, Trash2 } from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { fmtDateTime, toDate } from '@/features/shared/format'
import { cn } from '@/lib/utils'
import type { MailBoxApi } from '../hooks/useMailBox'
import { threadMessageCount } from '../mailThread'
import { GROUP_COLOR_CLASS, type MailMessage } from '../mailTypes'

type Props = {
  box: MailBoxApi
  message: MailMessage
  checked: boolean
  /** Toplu seçim açıkken avatar yerine onay kutusu görünür. */
  selecting: boolean
  onCheck: () => void
}

/** API `DD.MM.YYYY HH:mm:ss` (veya ISO) gönderir; çözülemeyen değer olduğu gibi gösterilir. */
const stamp = (value: string) => {
  const d = toDate(value)
  if (!d) return value
  return format(d, isToday(d) ? 'HH:mm' : 'd MMM', { locale: tr })
}

function RowAction({ label, onClick, children, className }: { label: string; onClick: () => void; children: ReactNode; className?: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e: MouseEvent) => {
        e.stopPropagation()
        onClick()
      }}
      className={cn('flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
    >
      {children}
    </button>
  )
}

export function MailListRow({ box, message: m, checked, selecting, onCheck }: Props) {
  const { t } = useTranslation()
  const active = box.selectedId === m.id
  const groups = box.groups.filter((g) => m.group_ids.includes(g.id))
  const name = m.from_name ?? m.from
  const threadCount = threadMessageCount(box.messages, m)
  const hasFiles = (m.attachments?.length ?? 0) > 0
  const inTrash = m.folder === 'TRASH'

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `mail:${m.id}`, data: { type: 'mail', messageId: m.id } })
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined

  return (
    <li ref={setNodeRef} style={style} className={cn('group relative', isDragging && 'z-20 opacity-60')} {...attributes} {...listeners}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => box.selectMessage(m.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') box.selectMessage(m.id)
        }}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'flex w-full cursor-pointer gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active ? 'bg-primary/10' : checked ? 'bg-primary/[0.06]' : 'hover:bg-muted/70',
        )}
      >
        <div className="relative mt-0.5 size-9 shrink-0">
          <Avatar name={name} className={cn('size-9 transition-opacity', (selecting || checked) && 'opacity-0', !selecting && 'group-hover:opacity-0')} />
          <span className={cn('absolute inset-0 flex items-center justify-center', selecting || checked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}>
            <input
              type="checkbox"
              checked={checked}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onChange={onCheck}
              className="size-4 cursor-pointer accent-[var(--color-primary)]"
              aria-label={t('mail.mailbox.bulk.select', { subject: m.subject })}
            />
          </span>
          {!m.is_read ? <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-card bg-primary" aria-label={t('mail.mailbox.unreadDot')} /> : null}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={cn('min-w-0 flex-1 truncate text-sm', !m.is_read ? 'font-semibold text-foreground' : 'font-medium text-foreground/80')}>{name}</span>
            {threadCount > 1 ? <span className="shrink-0 rounded-md bg-muted px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground">{threadCount}</span> : null}
            {hasFiles ? <Paperclip className="size-3.5 shrink-0 text-muted-foreground" aria-label={t('mail.mailbox.hasAttachments')} /> : null}
            {m.is_favorite ? <Star className="size-3.5 shrink-0 fill-warning text-warning" aria-label={t('mail.mailbox.folders.favorites')} /> : null}
            <time className={cn('shrink-0 text-[11px] tabular-nums md:group-hover:hidden', !m.is_read ? 'font-semibold text-primary' : 'text-muted-foreground')} dateTime={m.created_at} title={fmtDateTime(m.created_at)}>
              {stamp(m.created_at)}
            </time>
            <span className="-my-1 hidden shrink-0 items-center md:group-hover:flex">
              <RowAction label={m.is_read ? t('mail.mailbox.bulk.unread') : t('mail.mailbox.bulk.read')} onClick={() => box.setRead([m.id], !m.is_read)}>
                {m.is_read ? <MailIcon className="size-3.5" /> : <MailOpen className="size-3.5" />}
              </RowAction>
              <RowAction label={m.is_favorite ? t('mail.mailbox.unstar') : t('mail.mailbox.star')} onClick={() => box.toggleFavorite(m.id)}>
                <Star className={cn('size-3.5', m.is_favorite && 'fill-warning text-warning')} />
              </RowAction>
              {!inTrash ? (
                <RowAction label={t('mail.mailbox.actions.trash')} onClick={() => box.moveToFolder(m.id, 'TRASH')} className="hover:text-destructive">
                  <Trash2 className="size-3.5" />
                </RowAction>
              ) : null}
            </span>
          </div>
          <p className={cn('mt-0.5 truncate text-[13px]', !m.is_read ? 'font-medium text-foreground' : 'text-foreground/75')}>{m.subject || t('mail.mailbox.noSubject')}</p>
          <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{m.snippet}</p>
          {groups.length ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {groups.map((g) => (
                <span key={g.id} className={cn('rounded-md px-1.5 py-0.5 text-[10px] font-medium', GROUP_COLOR_CLASS[g.colorToken])}>
                  {g.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  )
}
